<template>
	<section class="page page-user" v-bind:key="userModel">
		<SceneUserProfile
			v-bind:favoriteCollection="favoriteCollection"
			v-bind:userModel="userModel"
			v-bind:venueCheckinCollection="venueCheckinCollection"
			v-if="userModel.uniqueKey"
		/>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import PageBase from '@/Core/Page.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * User profile page controller. Fetches user data and delegates
	 * presentation to SceneUserProfile.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Page
	 * @project ChalkySticks App
	 */
	@Component
	export default class PageUser extends PageBase {
		/**
		 * Collection of user's favorite venues
		 *
		 * @type ChalkySticks.Collection.Venue
		 */
		@Prop({
			default: () =>
				ChalkySticks.Factory.Venue.collection({
					limit: 32,
					qp: {
						reaction_from: '',
						reaction_type: ChalkySticks.Enum.ReactionType.Love,
					},
				}),
		})
		public favoriteCollection!: ChalkySticks.Collection.Venue;

		/**
		 * The user model
		 *
		 * @type ChalkySticks.Model.User
		 */
		@Prop({
			default: () => ChalkySticks.Factory.User.model(),
		})
		public userModel!: ChalkySticks.Model.User;

		/**
		 * Collection of user's recent venue checkins
		 *
		 * @type ChalkySticks.Collection.VenueCheckin
		 */
		@Prop({
			default: () =>
				new ChalkySticks.Collection.VenueCheckin({
					atRelationship: ['venue'],
					limit: 8,
				}),
		})
		public venueCheckinCollection!: ChalkySticks.Collection.VenueCheckin;

		/**
		 * Fetch user data and related collections on mount.
		 *
		 * @return Promise<void>
		 */
		@mounted
		protected async setup(): Promise<void> {
			const navigatingModel = this.$store.getters.navigatingUserModel;

			// Use navigating model from store if available (in-app navigation)
			if (navigatingModel && navigatingModel.getSlug() === this.$route.params.id) {
				this.userModel = navigatingModel;
			} else {
				this.userModel.set({
					id: this.$route.params.id,
				});
			}

			// Fetch user data
			await this.fetchUser();

			// Redirect to 404 if user not found
			if (!this.userModel?.id) {
				this.$router.push({
					path: '/not-found',
				});

				return;
			}

			// Fetch extras
			this.fetchFavorites();
			this.fetchVenueCheckins();
		}

		/**
		 * Fetch user's favorite venues.
		 *
		 * @return Promise<void>
		 */
		protected async fetchFavorites(): Promise<void> {
			this.favoriteCollection.setQueryParam('reaction_from', this.userModel.id);
			this.favoriteCollection.fetch();
		}

		/**
		 * Fetch user model data.
		 *
		 * @return Promise<void>
		 */
		protected async fetchUser(): Promise<void> {
			await this.userModel.fetch();
		}

		/**
		 * Fetch user's recent venue checkins.
		 *
		 * @return Promise<void>
		 */
		protected async fetchVenueCheckins(): Promise<void> {
			this.venueCheckinCollection.setQueryParam('user_id', this.userModel.id);
			this.venueCheckinCollection.fetch();
		}
	}
</script>

<style lang="scss">
	.page-user {
		background-color: var(--chalky-blue-4);
	}
</style>
